import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { useAsync } from '@/lib/useAsync'
import { useUnsavedChanges } from '@/lib/useUnsavedChanges'
import { AddressFields, TaxIdField } from '@/components/AddressFields'
import { LeaveGuard, useLeaveGuard } from '@/components/LeaveGuard'
import { AuditHistoryButton, AuditLogPanel } from '@/components/AuditLog'
import { PartyQuotations } from '@/components/PartyQuotations'
import { ResellerBank } from '@/components/ResellerBank'
import { ResellerCommission } from '@/components/ResellerCommission'
import { ResellerPortalUsers } from '@/components/ResellerPortalUsers'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Field, FormGrid, Input, SaveActions, Select } from '@/components/ui/form'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { SortableTh, Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  Paged,
  PartyStatus,
  ResellerDetail,
  ResellerDownline,
  ResellerListItem,
} from '@/types/api'

/**
 * Where a header's third click lands, and what the toolbar shows for it.
 * A real sort string rather than an empty one, so the two controls always agree.
 */
const DEFAULT_SORT = 'name'

export function Resellers() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const { current } = useCountry()
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<ResellerListItem | undefined>()

  const list = useAsync<Paged<ResellerListItem>>(
    (signal) =>
      api.get<Paged<ResellerListItem>>(
        '/resellers',
        { countryId: current?.countryId, search, includeInactive, sort, page, pageSize },
        signal,
      ),
    [current?.countryId, search, includeInactive, sort, page, pageSize],
  )

  function refine(apply: () => void) {
    apply()
    setPage(1)
  }

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Resellers"
        hint="Co-broke partners. A quotation names the one a deal came through, and their commission plan is rated against it."
        actions={
          can('reseller.create') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New reseller
            </Button>
          )
        }
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: 'name', label: 'Name A–Z' },
          { value: '-name', label: 'Name Z–A' },
          { value: 'status', label: 'Status' },
          { value: '-created', label: 'Newest first' },
        ]}
      >
        <div className="w-64">
          <Input
            placeholder="Search resellers"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        <FilterSelect
          label="Status"
          value={includeInactive ? 'all' : 'active'}
          onChange={(next) => refine(() => setIncludeInactive(next === 'all'))}
        >
          <option value="active">Active only</option>
          <option value="all">Active and inactive</option>
        </FilterSelect>
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {list.data.items.length === 0 ? (
              <EmptyState
                title="No resellers here yet"
                hint={`Nothing registered in ${current?.name ?? 'this country'}. Resellers are country-scoped, like everything else.`}
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh field="name" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Reseller
                    </SortableTh>
                    <Th>Contact</Th>
                    <Th>Sells under</Th>
                    <Th numeric>Quotations</Th>
                    <SortableTh field="status" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Status
                    </SortableTh>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((reseller) => (
                    <Tr key={reseller.id} onClick={() => navigate(`/resellers/${reseller.id}`)}>
                      <Td>
                        <span className="font-medium">{reseller.name}</span>
                        {reseller.registrationNo && (
                          <span className="ml-2 font-mono text-[12px] text-faint">
                            {reseller.registrationNo}
                          </span>
                        )}
                      </Td>
                      <Td className="text-muted">
                        {reseller.contactName ?? '-'}
                        {reseller.email && <span className="block text-[12px]">{reseller.email}</span>}
                      </Td>
                      <Td className="text-muted">
                        {reseller.parentResellerName ?? (
                          <span className="text-faint">&mdash;</span>
                        )}
                        {reseller.downlineCount > 0 && (
                          <span className="block text-[12px]">
                            {reseller.downlineCount} downline
                            {reseller.downlineCount === 1 ? '' : 's'}
                          </span>
                        )}
                      </Td>
                      <Td numeric>{reseller.quotationCount}</Td>
                      <Td>
                        <Badge tone={reseller.status === 'Active' ? 'positive' : 'neutral'}>
                          {reseller.status}
                        </Badge>
                      </Td>
                      <Td>
                        <span className="flex justify-end gap-2">
                          <AuditHistoryButton
                            entityType="Reseller"
                            entityId={reseller.id}
                            title={reseller.name}
                          />

                          {can('reseller.archive') && (
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={(event) => {
                                // The row itself opens the reseller; deleting it should
                                // not also navigate into what is about to disappear.
                                event.stopPropagation()
                                setDeleting(reseller)
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </span>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>

      <NewReseller
        open={creating}
        countryId={current?.countryId}
        onClose={() => setCreating(false)}
        onCreated={(name) => {
          setCreating(false)
          toast.show(`${name} added`, 'success')
          list.reload()
        }}
      />

      <DeleteReseller
        reseller={deleting}
        onClose={() => setDeleting(undefined)}
        onDeleted={(name) => {
          setDeleting(undefined)
          toast.show(`${name} deleted`, 'success')
          list.reload()
        }}
      />
    </>
  )
}

/**
 * Takes a reseller out of the catalogue.
 *
 * Not a removal, whatever the button says: a quotation raised through this reseller still
 * names them. What goes is the entry somebody could pick from - which is the whole of what
 * deleting one is for.
 */
function DeleteReseller({
  reseller,
  onClose,
  onDeleted,
}: {
  reseller: ResellerListItem | undefined
  onClose: () => void
  onDeleted: (name: string) => void
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  async function remove() {
    if (!reseller) return

    setBusy(true)
    try {
      await api.post(`/resellers/${reseller.id}/archive`)
      onDeleted(reseller.name)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not delete the reseller', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={reseller !== undefined}
      title={`Delete ${reseller?.name ?? 'this reseller'}?`}
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Keep it
          </Button>
          <Button size="sm" variant="danger" loading={busy} onClick={() => void remove()}>
            Delete
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2">
        {reseller && reseller.quotationCount > 0
          ? `${reseller.quotationCount} quotation${reseller.quotationCount === 1 ? '' : 's'} were raised through this reseller. Those keep naming them - what goes is the entry a rep can pick from.`
          : 'It stops being offered on new quotations. Nothing already raised changes.'}
      </p>
    </Dialog>
  )
}

/**
 * A reseller's own page.
 *
 * It was a dialog, which could not hold an address, a quotations list and a history
 * without becoming a form you scroll inside a modal. A record with this much on it gets a
 * page and a URL somebody can send to a colleague.
 */
/** What the detail form edits, and therefore what an unsaved change can be about. */
const EDITED = [
  'name',
  'registrationNo',
  'contactName',
  'email',
  'phone',
  'taxIdentificationNo',
  'status',
  'parentResellerId',
  'addressLine1',
  'addressLine2',
  'addressLine3',
  'postcode',
  'addressCountry',
] as const

export function ResellerDetailPage() {
  const { id } = useParams()
  return id ? (
    <Reseller resellerId={id} />
  ) : (
    <ErrorState title="No reseller" detail="That link is missing a reseller id." />
  )
}

function Reseller({ resellerId }: { resellerId: string }) {
  const { can } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [form, setForm] = useState<Partial<ResellerDetail>>({})
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<
    'details' | 'quotations' | 'commission' | 'bank' | 'portal' | 'history'
  >('details')

  const detail = useAsync<ResellerDetail>(
    (signal) =>
      api.get<ResellerDetail>(`/resellers/${resellerId}`, undefined, signal).then((loaded) => {
        setForm(loaded)
        return loaded
      }),
    [resellerId],
  )

  const apply = useCallback((values: Partial<ResellerDetail>) => setForm(values), [])

  const changes = useUnsavedChanges(form, detail.data, apply, EDITED)
  const guard = useLeaveGuard(changes)

  function set<K extends keyof ResellerDetail>(key: K, value: ResellerDetail[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const editable = can('reseller.edit')

  async function save() {
    setSaving(true)
    try {
      await api.patch(`/resellers/${resellerId}`, {
        name: form.name,
        registrationNo: form.registrationNo,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        addressLine3: form.addressLine3,
        postcode: form.postcode,
        addressCountry: form.addressCountry,
        taxIdentificationNo: form.taxIdentificationNo,
        status: form.status,
        parentResellerId: form.parentResellerId,

        // Null in a patch body means "leave it", the same as every other field here, so
        // removing a parent needs its own word for it.
        clearParent: form.parentResellerId === null && detail.data?.parentResellerId != null,
      })

      // Before the reload, so the page is clean the moment the save lands rather than
      // for as long as the round trip takes.
      changes.accept()
      toast.show('Reseller saved', 'success')
      detail.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save the reseller', 'error')
    } finally {
      setSaving(false)
    }
  }

  // Only the very first load replaces the screen. A reload after a save keeps what is
  // already on it and puts the spinner over the top - blanking a page somebody just
  // saved is the same jump as a loading block pushing a listing down.
  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) {
    return <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />
  }

  return (
    <>
      <PageHeader
        eyebrow={detail.data?.countryIsoCode}
        title={form.name ?? 'Reseller'}
        back={{ label: 'Back to resellers', onClick: () => navigate('/resellers') }}
      />

      <Tabs
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'quotations', label: 'Quotations', count: detail.data?.quotationCount },
          // Absent, not disabled, for anybody without the permission. See the note on the
          // Commission entry in AppShell: the reason is the partner's privacy.
          ...(can('reseller.commission.view')
            ? [{ id: 'commission' as const, label: 'Commission' }]
            : []),
          { id: 'bank', label: 'Bank' },
          { id: 'portal', label: 'Portal' },
          { id: 'history', label: 'History' },
        ]}
      />

      {tab === 'details' && (
        <>
          <Card className="mb-5">
            <CardHeader title="Reseller" />
            <FormGrid>
              <Field label="Name" required changed={changes.changed('name')}>
                {(id) => (
                  <Input id={id} disabled={!editable} value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} />
                )}
              </Field>
              <Field label="Registration no." changed={changes.changed('registrationNo')}>
                {(id) => (
                  <Input
                    id={id}
                    disabled={!editable}
                    value={form.registrationNo ?? ''}
                    onChange={(e) => set('registrationNo', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Contact name" changed={changes.changed('contactName')}>
                {(id) => (
                  <Input
                    id={id}
                    disabled={!editable}
                    value={form.contactName ?? ''}
                    onChange={(e) => set('contactName', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Email" changed={changes.changed('email')}>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    disabled={!editable}
                    value={form.email ?? ''}
                    onChange={(e) => set('email', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Phone" changed={changes.changed('phone')}>
                {(id) => (
                  <Input id={id} disabled={!editable} value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
                )}
              </Field>
              <TaxIdField
                value={form.taxIdentificationNo}
                onChange={(next) => set('taxIdentificationNo', next)}
                disabled={!editable}
                changed={changes.changed('taxIdentificationNo')}
                countryIsoCode={detail.data?.countryIsoCode}
              />
              <Field label="Status" changed={changes.changed('status')}>
                {(id) => (
                  <Select
                    id={id}
                    disabled={!editable}
                    value={form.status ?? 'Active'}
                    onChange={(e) => set('status', e.target.value as PartyStatus)}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Select>
                )}
              </Field>
            </FormGrid>
          </Card>

          <Card className="mb-5">
            <CardHeader
              title="Partner structure"
              hint="Who this reseller sells under, and who sells under them. Two levels, and no more."
              action={
                <Badge tone={detail.data?.parentResellerId ? 'neutral' : 'positive'}>
                  {detail.data?.parentResellerId ? 'Second level' : 'First level'}
                </Badge>
              }
            />
            <ParentField
              resellerId={resellerId}
              countryId={detail.data?.countryId}
              value={form.parentResellerId ?? null}
              downlineCount={detail.data?.downlines.length ?? 0}
              disabled={!editable}
              changed={changes.changed('parentResellerId')}
              onChange={(next) => set('parentResellerId', next)}
            />
            <Downlines downlines={detail.data?.downlines ?? []} />
          </Card>

          <Card>
            <CardHeader title="Address" hint="Where the post goes. Not the country this reseller belongs to." />
            <AddressFields value={form} onChange={set} disabled={!editable} changed={changes.changed} />
            {editable && (
              <SaveActions
                count={changes.fields.length}
                saving={saving}
                saveLabel="Save reseller"
                canSave={Boolean(form.name?.trim())}
                onSave={() => void save()}
                onDiscard={changes.discard}
              />
            )}
          </Card>
        </>
      )}

      {tab === 'quotations' && (
        <PartyQuotations
          filter={{ resellerId }}
          hint="Everything raised through this reseller that you have access to see."
        />
      )}

      {tab === 'commission' && (
        <ResellerCommission
          resellerId={resellerId}
          countryId={detail.data?.countryId}
          downlines={detail.data?.downlines ?? []}
        />
      )}

      {tab === 'bank' && <ResellerBank resellerId={resellerId} phoneOnFile={detail.data?.phone} />}

      {tab === 'portal' && <ResellerPortalUsers resellerId={resellerId} />}

      {tab === 'history' && <AuditLogPanel entityType="Reseller" entityId={resellerId} className="" />}

      <LeaveGuard guard={guard} />
    </>
  )
}

/**
 * The parent picker.
 *
 * <p>
 * Only a first-level partner in the same country can be chosen, and the list is filtered by
 * the API rather than in the browser - the two levels are enforced by the same code that reads
 * them. A partner that already has downlines of its own cannot be given a parent at all, and
 * the control says why rather than offering a choice that will be refused.
 * </p>
 */
function ParentField({
  resellerId,
  countryId,
  value,
  downlineCount,
  disabled,
  changed,
  onChange,
}: {
  resellerId: string
  countryId: string | undefined
  value: string | null
  downlineCount: number
  disabled: boolean
  changed: boolean
  onChange: (next: string | null) => void
}) {
  const candidates = useAsync<Paged<ResellerListItem>>(
    (signal) =>
      api.get<Paged<ResellerListItem>>(
        '/resellers',
        { countryId, firstLevelOnly: true, excludeId: resellerId, pageSize: 200, sort: 'name' },
        signal,
      ),
    [countryId, resellerId],
  )

  const blocked = downlineCount > 0

  return (
    <FormGrid>
      <Field
        label="Parent reseller"
        changed={changed}
        hint={
          blocked
            ? `This reseller already has ${downlineCount} downline${downlineCount === 1 ? '' : 's'} of its own. Move them first, or it would be a third level.`
            : 'Optional. Only a first-level reseller in the same country can be a parent.'
        }
      >
        {(id) => (
          <Select
            id={id}
            disabled={disabled || blocked}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          >
            <option value="">Sells directly with Codemax</option>
            {(candidates.data?.items ?? []).map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </FormGrid>
  )
}

/**
 * Who sells under this partner.
 *
 * Read-only, because a downline is edited from its own record - the parent is a field on the
 * child, and offering the same relationship from both ends is two ways to write one column.
 */
function Downlines({ downlines }: { downlines: readonly ResellerDownline[] }) {
  const navigate = useNavigate()

  if (downlines.length === 0) {
    return (
      <p className="border-t border-line-soft px-5 py-3 text-[13px] text-muted">
        Nobody sells under this reseller. Set the parent on a reseller&rsquo;s own record to put
        them here.
      </p>
    )
  }

  return (
    <Table>
      <thead>
        <tr>
          <Th>Downline</Th>
          <Th numeric>Quotations</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {downlines.map((downline) => (
          <Tr key={downline.id} onClick={() => navigate(`/resellers/${downline.id}`)}>
            <Td className="font-medium">{downline.name}</Td>
            <Td numeric>{downline.quotationCount}</Td>
            <Td>
              <Badge tone={downline.status === 'Active' ? 'positive' : 'neutral'}>
                {downline.status}
              </Badge>
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  )
}

function NewReseller({
  open,
  countryId,
  onClose,
  onCreated,
}: {
  open: boolean
  countryId: string | undefined
  onClose: () => void
  onCreated: (name: string) => void
}) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [contactName, setContactName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [saving, setSaving] = useState(false)

  async function create() {
    if (!countryId) return

    setSaving(true)
    try {
      await api.post('/resellers', {
        countryId,
        name,
        contactName: contactName || null,
        email: email || null,
        phone: phone || null,
      })

      onCreated(name)
      setName('')
      setContactName('')
      setEmail('')
      setPhone('')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not add the reseller', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="New reseller"
      description="Registered in the country you are working in."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!name || !countryId}
            onClick={() => void create()}
          >
            Add reseller
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" required>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Contact person">
          {(id) => <Input id={id} value={contactName} onChange={(e) => setContactName(e.target.value)} />}
        </Field>
        <Field label="Email">
          {(id) => <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label="Phone">
          {(id) => <Input id={id} value={phone} onChange={(e) => setPhone(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  )
}
